/**
 * module-config-api.service.ts - API client for module configuration
 *
 * @remarks
 * Proporciona métodos para gestionar la configuración de módulos por proyecto.
 * Permite configurar umbrales de asociación y comportamiento por entidad.
 *
 * @see ARQUITECTURA_ASOCIACION_PUNTO_RED.md
 */

// API_URL ya incluye /api/v1, NO agregar /v1 de nuevo en las URLs
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * Configuración de una entidad específica
 */
export interface ModuleEntityConfig {
  /** Distancia máxima para asociar a un edge (metros) */
  edgeThresholdMeters?: number;
  /** Distancia máxima para asociar a un nodo (metros) */
  nodeThresholdMeters?: number;
  /** Si true, asocia automáticamente al crear la entidad */
  autoAssociateOnCreate?: boolean;
  /** Si true, recalcula asociación cuando la red cambia */
  autoRecalculateOnNetworkChange?: boolean;
  /** Si true, permite múltiples conexiones a la red por entidad */
  allowMultipleConnections?: boolean;
  /** Si true, auto-conecta a todos los edges dentro del radio al crear (solo si allowMultipleConnections=true) */
  autoConnectAllEdgesInRadius?: boolean;
}

/**
 * Información de una entidad en la respuesta de configuración
 */
export interface EntityConfigInfo {
  /** Configuración actual (puede ser específica o heredada) */
  config: ModuleEntityConfig;
  /** Indica si el valor es específico del proyecto o heredado del default */
  isCustom: boolean;
  /** Nombre legible de la entidad */
  label: string;
  /** Icono de la entidad */
  icon: string;
}

/**
 * Respuesta de configuración de módulo
 */
export interface ModuleConfigResponse {
  /** ID del módulo */
  moduleId: string;
  /** Configuraciones por entidad con valores efectivos (incluye herencia) */
  entities: Record<string, EntityConfigInfo>;
}

/**
 * Cliente API para configuración de módulos
 */
export const moduleConfigApi = {
  /**
   * Obtener configuración de un módulo
   *
   * @param projectId - ID del proyecto
   * @param moduleId - ID del módulo (environmental, waste, mobility, etc.)
   * @returns Configuración del módulo con valores efectivos
   */
  async getConfig(
    projectId: string,
    moduleId: string,
  ): Promise<ModuleConfigResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/config/${moduleId}`;

    console.log('[moduleConfigApi.getConfig] Fetching:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[moduleConfigApi.getConfig] Error:', errorBody);
      throw new Error(`Failed to get module config: ${response.status} - ${errorBody}`);
    }

    return response.json();
  },

  /**
   * Actualizar configuración de una entidad específica
   *
   * @param projectId - ID del proyecto
   * @param moduleId - ID del módulo
   * @param entityType - Tipo de entidad (tree, fountain, container, etc.)
   * @param config - Nueva configuración
   * @returns Configuración actualizada del módulo
   */
  async updateEntityConfig(
    projectId: string,
    moduleId: string,
    entityType: string,
    config: ModuleEntityConfig,
  ): Promise<ModuleConfigResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/config/${moduleId}/${entityType}`;

    console.log('[moduleConfigApi.updateEntityConfig] Updating:', url, config);

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[moduleConfigApi.updateEntityConfig] Error:', errorBody);
      throw new Error(`Failed to update entity config: ${response.status} - ${errorBody}`);
    }

    return response.json();
  },

  /**
   * Resetear configuración de una entidad a valores por defecto
   *
   * @param projectId - ID del proyecto
   * @param moduleId - ID del módulo
   * @param entityType - Tipo de entidad
   * @returns Configuración actualizada del módulo (con valores default)
   */
  async resetEntityConfig(
    projectId: string,
    moduleId: string,
    entityType: string,
  ): Promise<ModuleConfigResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/config/${moduleId}/${entityType}`;

    console.log('[moduleConfigApi.resetEntityConfig] Resetting:', url);

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[moduleConfigApi.resetEntityConfig] Error:', errorBody);
      throw new Error(`Failed to reset entity config: ${response.status} - ${errorBody}`);
    }

    return response.json();
  },
};
